import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useAtomValue } from "jotai";
import { AlertTriangle, X } from "lucide-react";
import { authAtom } from "../stores/auth";
import { localeAtom } from "../stores/locale";
import { t, interp } from "../lib/i18n";
import { billingApi, orgApi } from "../api/client";

// FreeIncludedSeats — the free-tier seat ceiling (pricing-v2.md: 5席=$0). Kept in
// sync with the Go authmodel.FreeIncludedSeats; only used to quantify the
// over-seat count in the lapsed notice.
const FREE_SEATS = 5;

// Sub statuses that mean a paid plan has LAPSED to free (the account entitlement
// is already free — billing.get returns plan="free" — but the sub row carries
// one of these). "active"/absent = a genuinely-free org, not lapsed.
const LAPSED = new Set(["past_due", "unpaid", "canceled"]);

// LapsedBanner shows a global notice when an ORG's Nova plan has lapsed (payment
// failure or cancellation). Admins get the fix-it CTAs + the quantified fallout
// (over-seat members locked out, private repos capped, Nova features off); other
// members get a one-line read-only note so they understand why things changed.
//
// Detection reads billing.get, which now reports the ENTITLEMENT plan (free) plus
// the sub status — so "lapsed Nova" (free + past_due/canceled) is distinguishable
// from "never paid" (free + active). past_due/unpaid is recoverable (fix the
// card, Stripe auto-restores); canceled needs a re-subscribe.
export default function LapsedBanner() {
  const { account = "" } = useParams<{ account: string }>();
  const auth = useAtomValue(authAtom);
  const locale = useAtomValue(localeAtom);
  const navigate = useNavigate();
  const isOrg = !!account && auth.user?.login !== account;

  const [status, setStatus] = useState<string | null>(null); // lapsed sub status, else null
  const [isAdmin, setIsAdmin] = useState(false);
  const [overSeats, setOverSeats] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setStatus(null);
    setIsAdmin(false);
    setOverSeats(0);
    setDismissed(false);
    if (!isOrg) return;
    const ac = new AbortController();
    billingApi
      .get(account)
      .then(async (b) => {
        if (ac.signal.aborted) return;
        if (b.plan !== "free" || !LAPSED.has(b.status)) return; // not a lapsed org
        setStatus(b.status);
        const orgs = await orgApi.memberships().catch(() => []);
        if (ac.signal.aborted) return;
        const admin = orgs.find((o) => o.login === account)?.role === "admin";
        setIsAdmin(admin);
        if (admin) {
          // seats endpoint is admin-only; member_count vs the 5 free seats gives
          // how many members are locked out of the private observatory.
          const s = await billingApi.seats(account).catch(() => null);
          if (!ac.signal.aborted && s) setOverSeats(Math.max(0, s.member_count - FREE_SEATS));
        }
      })
      .catch(() => {});
    return () => ac.abort();
  }, [account, isOrg]);

  if (!status || dismissed) return null;

  const recoverable = status !== "canceled";
  const titleKey = recoverable ? "lapsedBanner.title.pastDue" : "lapsedBanner.title.canceled";
  const bodyKey = recoverable ? "lapsedBanner.body.pastDue" : "lapsedBanner.body.canceled";

  const openPortal = () => {
    billingApi
      .portal(account)
      .then((r) => {
        if (r?.url) window.location.href = r.url;
      })
      .catch(() => navigate(`/${account}/plan`));
  };

  return (
    <div
      className="mb-lg flex items-start gap-md rounded-lg p-lg"
      style={{ border: "1px solid var(--gold)", background: "var(--surface-sunken)" }}
      role="alert"
    >
      <AlertTriangle size={18} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
          {t(titleKey, locale)}
        </p>

        {isAdmin ? (
          <>
            <p className="mt-xs text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
              {t(bodyKey, locale)}
            </p>
            <ul className="mt-sm space-y-0.5 text-xs" style={{ color: "var(--text-muted)" }}>
              {overSeats > 0 && <li>{interp(t("lapsedBanner.overSeats", locale), { over: overSeats, free: FREE_SEATS })}</li>}
              <li>{t("lapsedBanner.overRepos", locale)}</li>
              <li>{t("lapsedBanner.featuresOff", locale)}</li>
            </ul>
            <div className="mt-md flex flex-wrap gap-sm">
              {recoverable ? (
                <button
                  onClick={openPortal}
                  className="btn-gold rounded-md px-lg py-xs text-xs"
                >
                  {t("lapsedBanner.cta.fixPayment", locale)}
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/${account}/plan`)}
                  className="btn-gold rounded-md px-lg py-xs text-xs"
                >
                  {t("lapsedBanner.cta.resume", locale)}
                </button>
              )}
              <button
                onClick={() => navigate(`/${account}/members`)}
                className="rounded-md px-lg py-xs text-xs font-medium"
                style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}
              >
                {t("lapsedBanner.cta.review", locale)}
              </button>
            </div>
          </>
        ) : (
          <p className="mt-xs text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
            {t("lapsedBanner.memberNote", locale)}
          </p>
        )}
      </div>

      <button
        onClick={() => setDismissed(true)}
        aria-label={t("common.dismiss", locale)}
        style={{ color: "var(--text-dim)", flexShrink: 0 }}
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
